import React from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import { APP_PATH_LOGIN, APP_PATH_RESEND_ACTIVATION, MAIN_APP_NAME } from '../../config';
import { useAccountActivationWithRecaptcha } from '../../hooks/useAccountActivationWithRecaptcha';
import RecaptchaBadge from '../../components/RecaptchaBadge';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  card: {
    maxWidth: 600,
    width: '100%'
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3)
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  },
  successIcon: {
    fontSize: 80,
    color: theme.palette.success.main
  },
  errorIcon: {
    fontSize: 80,
    color: theme.palette.error.main
  },
  infoIcon: {
    fontSize: 80,
    color: theme.palette.info.main
  },
  loadingIcon: {
    color: theme.palette.primary.main
  },
  content: {
    textAlign: 'center'
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  message: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius
  }
}));

const AccountActivationWithRecaptcha = () => {
  const classes = useStyles();
  const history = useHistory();
  const { handleActivate, isLoading, isSuccess, isError, errorMessage, token } =
    useAccountActivationWithRecaptcha();

  const handleGoToLogin = () => {
    history.push(APP_PATH_LOGIN);
  };

  const handleGoToResend = () => {
    history.push(APP_PATH_RESEND_ACTIVATION);
  };

  const renderContent = () => {
    if (!token) {
      return (
        <>
          <div className={classes.iconContainer}>
            <ErrorIcon className={classes.errorIcon} />
          </div>
          <Typography variant="h3" gutterBottom>
            Invalid Activation Link
          </Typography>
          <Typography color="textSecondary" variant="body1">
            The activation link you used is invalid or missing. Please check
            your email and click on the correct activation link.
          </Typography>
          <div className={classes.actions}>
            <Button
              color="primary"
              fullWidth
              size="large"
              variant="contained"
              onClick={handleGoToResend}>
              Request New Activation Link
            </Button>
            <Typography color="textSecondary" variant="body2">
              Already activated?{' '}
              <RouterLink to={APP_PATH_LOGIN}>Sign in</RouterLink>
            </Typography>
          </div>
        </>
      );
    }

    if (isLoading) {
      return (
        <>
          <div className={classes.iconContainer}>
            <CircularProgress
              size={80}
              thickness={2}
              className={classes.loadingIcon}
            />
          </div>
          <Typography variant="h3" gutterBottom>
            Activating Your Account
          </Typography>
          <Typography color="textSecondary" variant="body1">
            Please wait while we activate your account...
          </Typography>
        </>
      );
    }

    if (isSuccess) {
      return (
        <>
          <div className={classes.iconContainer}>
            <CheckCircleIcon className={classes.successIcon} />
          </div>
          <Typography variant="h3" gutterBottom>
            Account Activated Successfully!
          </Typography>
          <Typography color="textSecondary" variant="body1" gutterBottom>
            Your account has been successfully activated. You can now sign in
            and start building your world-class remote team.
          </Typography>
          <Box className={classes.message}>
            <Typography variant="body2" color="textSecondary">
              Welcome to {MAIN_APP_NAME}! We're excited to have you on board.
            </Typography>
          </Box>
          <div className={classes.actions}>
            <Button
              color="primary"
              fullWidth
              size="large"
              variant="contained"
              onClick={handleGoToLogin}>
              Sign In to Your Account
            </Button>
          </div>
        </>
      );
    }

    if (isError) {
      const isExpired = errorMessage?.toLowerCase().includes('expired');
      const isAlreadyActivated = errorMessage
        ?.toLowerCase()
        .includes('already activated');

      return (
        <>
          <div className={classes.iconContainer}>
            <ErrorIcon className={classes.errorIcon} />
          </div>
          <Typography variant="h3" gutterBottom>
            {isAlreadyActivated
              ? 'Account Already Activated'
              : 'Activation Failed'}
          </Typography>
          <Typography color="textSecondary" variant="body1" gutterBottom>
            {isAlreadyActivated
              ? 'Your account has already been activated. You can sign in now.'
              : isExpired
              ? 'Your activation link has expired. Activation links are valid for 24 hours.'
              : errorMessage ||
                'We encountered an error while activating your account.'}
          </Typography>
          <div className={classes.actions}>
            <Button
              color="primary"
              fullWidth
              size="large"
              variant="contained"
              onClick={handleGoToLogin}>
              Sign In
            </Button>
            {!isAlreadyActivated && (
              <Button
                color="primary"
                fullWidth
                size="medium"
                variant="outlined"
                onClick={handleGoToResend}>
                Request New Activation Link
              </Button>
            )}
          </div>
        </>
      );
    }

    // Default state - show activation button
    return (
      <>
        <div className={classes.iconContainer}>
          <InfoIcon className={classes.infoIcon} />
        </div>
        <Typography variant="h3" gutterBottom>
          Activate Your Account
        </Typography>
        <Typography color="textSecondary" variant="body1" gutterBottom>
          Click the button below to activate your account and start using {MAIN_APP_NAME}.
        </Typography>
        <Box className={classes.message}>
          <Typography variant="body2" color="textSecondary">
            <span role="img" aria-label="lock">🔒</span> This activation is protected by reCAPTCHA for your security.
          </Typography>
        </Box>
        <div className={classes.actions}>
          <Button
            color="primary"
            fullWidth
            size="large"
            variant="contained"
            onClick={handleActivate}
            data-test="activate-account-button">
            Activate My Account
          </Button>
          <Typography color="textSecondary" variant="body2">
            Already activated?{' '}
            <RouterLink to={APP_PATH_LOGIN}>Sign in</RouterLink>
          </Typography>
        </div>
        <RecaptchaBadge />
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <Typography variant="h2" color="textPrimary">
              {MAIN_APP_NAME}
            </Typography>
          </div>
          <div className={classes.content}>{renderContent()}</div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountActivationWithRecaptcha;

